import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Eye, Target, MoreVertical } from 'lucide-react';
import { MetricCell, MetricColor } from './MetricCell';
import { ActivityCell, ActivityColor } from './ActivityCell';
import { StreakDisplay } from './StreakDisplay';
import { WeightGraphModal } from './WeightGraphModal';
import { GoalCompletionChartModal } from './GoalCompletionChartModal';
import { ActivityMetricsTooltip } from './ActivityMetricsTooltip';
import { StatusFilterButtons } from './StatusFilterButtons';
import { ClientStatus } from './StatusBadge';

interface ClientMetrics {
  total_loss: {
    value: number;
    color: MetricColor;
    unit: string;
    metadata: any;
  };
  daily_weight: {
    value: number;
    color: MetricColor;
    stale: boolean;
    unit: string;
    metadata: any;
  };
  daily_change: {
    value: number;
    color: MetricColor;
    stale: boolean;
    unit: string;
    metadata: any;
  };
  weekly_average: {
    value: number;
    color: MetricColor;
    stale: boolean;
    unit: string;
    metadata: any;
  };
  weekly_change: {
    value: number;
    color: MetricColor;
    stale: boolean;
    unit: string;
    metadata: any;
  };
  streak: {
    value: number;
    color: MetricColor;
    unit: string;
    metadata: any;
  };
  activity_status: {
    color: ActivityColor;
    days_since: number;
    last_activity_date: string;
    last_activity_timestamp: string; // Full ISO timestamp for hours calculation
    metadata: any;
  };
  activity_metrics?: {
    days_since_last_entry: number | null;
    last_entry_date: string | null;
    entries_last_7_days: number;
    entries_last_lookback: number;
    lookback_period: number;
    engagement_rate_7d: number;
    engagement_rate_lookback: number;
    trend: 'improving' | 'stable' | 'declining';
    goal_performance: any[];
    overall_goal_completion: number;
  };
  client_status?: {
    status: string;
    badge: string;
    color: string;
    icon: string;
  };
}

interface ClientSummary {
  client_id: string;
  first_name: string;
  last_name: string;
  email: string;
  metrics: ClientMetrics;
  boosting: {
    enabled: boolean;
    last_boost_date: string | null;
  };
  last_note: {
    timestamp: string | null;
    staff_name: string | null;
  };
  calculated_at: string;
  success: boolean;
}

interface StatusCount {
  status: ClientStatus;
  label: string;
  icon: string;
  count: number;
}

interface ClientSummaryTableProps {
  scope: 'my-clients' | 'all-clients';
  onCountUpdate?: (count: number) => void;
  onStatusCountsUpdate?: (counts: StatusCount[]) => void;
}

/**
 * ClientSummaryTable - Coach Dashboard "My Clients" table
 * Phase 3 Implementation with all 11 columns
 *
 * Columns:
 * 1. Info/Name - Client identification
 * 2. Streak - Consecutive green weeks
 * 3. Total Loss - starting_weight - current_weight
 * 4. Weight - Current weight with stale data protocol
 * 5. Daily Change - Current - previous weight
 * 6. Weekly Avg - Current week average
 * 7. Weekly Change - Current week - previous week
 * 8. Boosting - (Phase 4 placeholder)
 * 9. Last Note - (Phase 4 placeholder)
 * 10. Activity - Relative time + date with color coding
 * 11. Actions - Context menu
 */
export const ClientSummaryTable: React.FC<ClientSummaryTableProps> = ({
  scope,
  onCountUpdate,
  onStatusCountsUpdate
}) => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<ClientSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [activeStatusFilter, setActiveStatusFilter] = useState<ClientStatus | 'all'>('all');

  // Weight graph modal state
  const [weightGraphModal, setWeightGraphModal] = useState<{
    isOpen: boolean;
    clientId: string;
    clientName: string;
    mode: 'daily' | 'weekly';
  }>({
    isOpen: false,
    clientId: '',
    clientName: '',
    mode: 'daily'
  });

  // Goal completion chart modal state
  const [goalCompletionModal, setGoalCompletionModal] = useState<{
    isOpen: boolean;
    clientId: string;
    clientName: string;
  }>({
    isOpen: false,
    clientId: '',
    clientName: ''
  });

  // Activity metrics tooltip state
  const [activityTooltip, setActivityTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    data: any;
    clientStatus: any;
    clientName: string;
  }>({
    visible: false,
    x: 0,
    y: 0,
    data: null,
    clientStatus: null,
    clientName: ''
  });

  useEffect(() => {
    loadClients();
  }, [scope]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const loadClients = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use different endpoint based on scope
      const endpoint = scope === 'all-clients'
        ? '/api/coach/dashboard/all'
        : '/api/coach/dashboard/summary';

      const response = await fetch(endpoint, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setClients(data.clients || []);

        // Update parent count
        if (onCountUpdate) {
          onCountUpdate(data.clients?.length || 0);
        }
      } else {
        const errorData = await response.json();
        console.error('Failed to load dashboard summary:', errorData);
        setError(errorData.error || 'Failed to load client data');
        setClients([]);
      }
    } catch (error) {
      console.error('Error loading dashboard summary:', error);
      setError('Network error - could not load client data');
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredClients = () => {
    return clients.filter(client => {
      // Search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = searchTerm === '' ||
        client.first_name.toLowerCase().includes(searchLower) ||
        client.last_name.toLowerCase().includes(searchLower) ||
        client.email.toLowerCase().includes(searchLower);

      // Status filter (Phase 4)
      const matchesStatus = activeStatusFilter === 'all' ||
        client.metrics?.client_status?.status === activeStatusFilter;

      return matchesSearch && matchesStatus;
    });
  };

  // Calculate status counts for filter buttons (Phase 4)
  const getStatusCounts = () => {
    const counts = {
      inactive: 0,
      needs_attention: 0,
      at_risk: 0,
      excelling: 0,
      active_on_track: 0
    };

    clients.forEach(client => {
      const status = client.metrics?.client_status?.status;
      if (status && status in counts) {
        counts[status as ClientStatus]++;
      }
    });

    return [
      { status: 'excelling' as ClientStatus, label: 'Excelling', icon: '🟢', count: counts.excelling },
      { status: 'active_on_track' as ClientStatus, label: 'Active', icon: '🔵', count: counts.active_on_track },
      { status: 'needs_attention' as ClientStatus, label: 'Attn', icon: '🟡', count: counts.needs_attention },
      { status: 'at_risk' as ClientStatus, label: 'At Risk', icon: '🟠', count: counts.at_risk },
      { status: 'inactive' as ClientStatus, label: 'Inactive', icon: '🔴', count: counts.inactive }
    ];
  };

  const handleClientClick = (clientId: string) => {
    navigate(`/staff/client/${clientId}`);
  };

  const handleOpenWeightGraph = (
    clientId: string,
    clientName: string,
    mode: 'daily' | 'weekly'
  ) => {
    setWeightGraphModal({
      isOpen: true,
      clientId,
      clientName,
      mode
    });
  };

  const handleCloseWeightGraph = () => {
    setWeightGraphModal({
      isOpen: false,
      clientId: '',
      clientName: '',
      mode: 'daily'
    });
  };

  const filteredClients = getFilteredClients();
  const statusCounts = getStatusCounts();

  // Update parent with status counts (Phase 4)
  React.useEffect(() => {
    if (onStatusCountsUpdate) {
      const counts = getStatusCounts();
      onStatusCountsUpdate(counts);
    }
  }, [clients, onStatusCountsUpdate]);

  return (
    <>
      {/* Search and Controls */}
      <div className="controls">
        <div className="search-box">
          <Search className="search-icon" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="controls-actions">
          <button className="refresh-btn" onClick={loadClients} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh Data'}
          </button>
        </div>
      </div>

      {/* Phase 4: Status Filter Buttons */}
      {!loading && !error && clients.length > 0 && (
        <StatusFilterButtons
          statusCounts={statusCounts}
          activeFilter={activeStatusFilter}
          onFilterChange={setActiveStatusFilter}
          totalCount={clients.length}
          filteredCount={filteredClients.length}
        />
      )}

      {/* Table */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading client metrics...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <h3>Error Loading Data</h3>
          <p>{error}</p>
          <button className="retry-btn" onClick={loadClients}>
            Retry
          </button>
        </div>
      ) : filteredClients.length === 0 ? (
        <div className="empty-state">
          {searchTerm ? (
            <>
              <Search className="empty-icon" />
              <h3>No clients found</h3>
              <p>Try adjusting your search</p>
            </>
          ) : (
            <>
              <Target className="empty-icon" />
              <h3>No clients assigned yet</h3>
              <p>Contact your administrator to get clients assigned to you</p>
            </>
          )}
        </div>
      ) : (
        <div className="table-container">
          <div className="table-scroll">
            <table className="summary-table">
              <thead>
                <tr>
                  <th className="col-name">Client</th>
                  <th className="col-streak">Streak</th>
                  <th className="col-total-loss">Total Loss</th>
                  <th className="col-weight">Weight</th>
                  <th className="col-daily-change">Daily Δ</th>
                  <th className="col-weekly-avg">Weekly Avg</th>
                  <th className="col-weekly-change">Weekly Δ</th>
                  <th className="col-boosting">Boosting</th>
                  <th className="col-last-note">Last Note</th>
                  <th className="col-activity">Status</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((client) => (
                  <tr key={client.client_id} className="data-row">
                    {/* Column 1: Name */}
                    <td className="col-name">
                      <button
                        className="client-name-btn"
                        onClick={() => handleClientClick(client.client_id)}
                      >
                        <div className="client-name-primary">
                          {client.first_name} {client.last_name}
                        </div>
                        <div className="client-email">{client.email}</div>
                      </button>
                    </td>

                    {/* Column 2: Streak - Clickable to open weekly weight graph */}
                    <td className="col-streak">
                      {client.metrics?.streak ? (
                        <StreakDisplay
                          streakValue={client.metrics.streak.value}
                          color={client.metrics.streak.color as any}
                          lastCompletedWeek={client.metrics.streak.metadata?.last_completed_week}
                          weekStatus={client.metrics.streak.metadata?.week_status}
                          onClick={() => handleOpenWeightGraph(
                            client.client_id,
                            `${client.first_name} ${client.last_name}`,
                            'weekly'
                          )}
                          tooltip="Click to view weekly average weights over time"
                        />
                      ) : (
                        <span className="no-data">--</span>
                      )}
                    </td>

                    {/* Column 3: Total Loss - Clickable to open daily weight graph */}
                    <td className="col-total-loss">
                      {client.metrics?.total_loss ? (
                        <MetricCell
                          value={client.metrics.total_loss.value > 0 ? `-${client.metrics.total_loss.value}` : client.metrics.total_loss.value < 0 ? `+${Math.abs(client.metrics.total_loss.value)}` : client.metrics.total_loss.value}
                          unit={client.metrics.total_loss.unit}
                          color={client.metrics.total_loss.color}
                          onClick={() => handleOpenWeightGraph(
                            client.client_id,
                            `${client.first_name} ${client.last_name}`,
                            'daily'
                          )}
                          tooltip="Click to view weight over time"
                        />
                      ) : (
                        <span className="no-data">--</span>
                      )}
                    </td>

                    {/* Column 4: Current Weight - Clickable to open daily weight graph */}
                    <td className="col-weight">
                      {client.metrics?.daily_weight ? (
                        <MetricCell
                          value={client.metrics.daily_weight.value}
                          unit={client.metrics.daily_weight.unit}
                          color={client.metrics.daily_weight.color}
                          stale={client.metrics.daily_weight.stale}
                          onClick={() => handleOpenWeightGraph(
                            client.client_id,
                            `${client.first_name} ${client.last_name}`,
                            'daily'
                          )}
                          tooltip={
                            client.metrics.daily_weight.stale
                              ? `Stale data from ${client.metrics.daily_weight.metadata?.date}. Click to view weight over time.`
                              : `Current weight as of ${client.metrics.daily_weight.metadata?.date}. Click to view weight over time.`
                          }
                        />
                      ) : (
                        <span className="no-data">--</span>
                      )}
                    </td>

                    {/* Column 5: Daily Change */}
                    <td className="col-daily-change">
                      {client.metrics?.daily_change ? (
                        <MetricCell
                          value={client.metrics.daily_change.value > 0 ? `+${client.metrics.daily_change.value}` : client.metrics.daily_change.value}
                          unit={client.metrics.daily_change.unit}
                          color={client.metrics.daily_change.color}
                          stale={client.metrics.daily_change.stale}
                        />
                      ) : (
                        <span className="no-data">--</span>
                      )}
                    </td>

                    {/* Column 6: Weekly Average - Clickable to open weekly weight graph */}
                    <td className="col-weekly-avg">
                      {client.metrics?.weekly_average ? (
                        <MetricCell
                          value={client.metrics.weekly_average.value}
                          unit={client.metrics.weekly_average.unit}
                          color={client.metrics.weekly_average.color}
                          stale={client.metrics.weekly_average.stale}
                          onClick={() => handleOpenWeightGraph(
                            client.client_id,
                            `${client.first_name} ${client.last_name}`,
                            'weekly'
                          )}
                          tooltip="Click to view weekly average weights over time"
                        />
                      ) : (
                        <span className="no-data">--</span>
                      )}
                    </td>

                    {/* Column 7: Weekly Change - Clickable to open weekly weight graph */}
                    <td className="col-weekly-change">
                      {client.metrics?.weekly_change ? (
                        <MetricCell
                          value={client.metrics.weekly_change.value > 0 ? `+${client.metrics.weekly_change.value}` : client.metrics.weekly_change.value}
                          unit={client.metrics.weekly_change.unit}
                          color={client.metrics.weekly_change.color}
                          stale={client.metrics.weekly_change.stale}
                          onClick={() => handleOpenWeightGraph(
                            client.client_id,
                            `${client.first_name} ${client.last_name}`,
                            'weekly'
                          )}
                          tooltip="Click to view weekly average weights over time"
                        />
                      ) : (
                        <span className="no-data">--</span>
                      )}
                    </td>

                    {/* Column 8: Boosting (Phase 4 placeholder) */}
                    <td className="col-boosting">
                      <span className="no-data">—</span>
                    </td>

                    {/* Column 9: Last Note (Phase 4 placeholder) */}
                    <td className="col-last-note">
                      <span className="no-data">—</span>
                    </td>

                    {/* Column 10: Activity */}
                    <td
                      className="col-activity"
                      onClick={() => {
                        setGoalCompletionModal({
                          isOpen: true,
                          clientId: client.client_id,
                          clientName: `${client.first_name} ${client.last_name}`
                        });
                      }}
                      onMouseEnter={(e) => {
                        if (client.metrics?.activity_metrics) {
                          setActivityTooltip({
                            visible: true,
                            x: e.clientX,
                            y: e.clientY,
                            data: client.metrics.activity_metrics,
                            clientStatus: client.metrics.client_status || null,
                            clientName: `${client.first_name} ${client.last_name}`
                          });
                        }
                      }}
                      onMouseLeave={() => {
                        setActivityTooltip(prev => ({ ...prev, visible: false }));
                      }}
                      onMouseMove={(e) => {
                        if (activityTooltip.visible) {
                          setActivityTooltip(prev => ({
                            ...prev,
                            x: e.clientX,
                            y: e.clientY
                          }));
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {client.metrics?.activity_status ? (
                        <ActivityCell
                          lastActivityDate={client.metrics.activity_status.last_activity_date}
                          lastActivityTimestamp={client.metrics.activity_status.last_activity_timestamp}
                          daysSince={client.metrics.activity_status.days_since}
                          color={client.metrics.activity_status.color}
                        />
                      ) : (
                        <span className="no-data">--</span>
                      )}
                    </td>

                    {/* Column 11: Actions */}
                    <td className="col-actions">
                      <div className="action-buttons">
                        <button
                          className="action-btn"
                          onClick={() => handleClientClick(client.client_id)}
                          title="View Client Dashboard"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          className="action-btn"
                          onClick={() => handleClientClick(client.client_id)}
                          title="More Options"
                        >
                          <MoreVertical size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Weight Graph Modal */}
      <WeightGraphModal
        clientId={weightGraphModal.clientId}
        clientName={weightGraphModal.clientName}
        mode={weightGraphModal.mode}
        isOpen={weightGraphModal.isOpen}
        onClose={handleCloseWeightGraph}
      />

      {/* Goal Completion Chart Modal */}
      <GoalCompletionChartModal
        clientId={goalCompletionModal.clientId}
        clientName={goalCompletionModal.clientName}
        isOpen={goalCompletionModal.isOpen}
        onClose={() => setGoalCompletionModal({ isOpen: false, clientId: '', clientName: '' })}
      />

      {/* Activity Metrics Tooltip */}
      <ActivityMetricsTooltip
        visible={activityTooltip.visible}
        x={activityTooltip.x}
        y={activityTooltip.y}
        data={activityTooltip.data}
        clientStatus={activityTooltip.clientStatus}
        clientName={activityTooltip.clientName}
      />

      <style>{`
        .controls {
          padding: 20px;
          border-bottom: 1px solid var(--border);
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          background: var(--card-bg);
        }

        .search-box {
          position: relative;
          flex: 1;
          max-width: 400px;
        }

        .search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          width: 18px;
          height: 18px;
          color: var(--text-muted);
        }

        .search-box input {
          width: 100%;
          padding: 10px 12px 10px 38px;
          border: 1px solid var(--border);
          border-radius: 6px;
          font-size: 14px;
          background: var(--input-bg);
          color: var(--text-primary);
        }

        .search-box input:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.1);
        }

        .controls-actions {
          display: flex;
          gap: 8px;
        }

        .refresh-btn {
          padding: 10px 20px;
          border: 1px solid var(--border);
          border-radius: 6px;
          background: var(--card-bg);
          color: var(--text-secondary);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .refresh-btn:hover:not(:disabled) {
          background: var(--bg-secondary);
          color: var(--text-primary);
        }

        .refresh-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .loading-state,
        .empty-state,
        .error-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          text-align: center;
          background: var(--card-bg);
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid var(--border);
          border-top-color: var(--primary);
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin-bottom: 16px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .loading-state p {
          color: var(--text-secondary);
          font-size: 14px;
          margin: 0;
        }

        .error-state h3 {
          color: #d32f2f;
          margin-bottom: 8px;
        }

        .error-state p {
          color: var(--text-secondary);
          margin-bottom: 16px;
        }

        .retry-btn {
          padding: 10px 20px;
          background: var(--primary);
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
        }

        .retry-btn:hover {
          opacity: 0.9;
        }

        .empty-icon {
          width: 48px;
          height: 48px;
          color: var(--text-muted);
          margin-bottom: 16px;
          opacity: 0.5;
        }

        .empty-state h3 {
          color: var(--text-primary);
          font-size: 18px;
          font-weight: 600;
          margin: 0 0 8px 0;
        }

        .empty-state p {
          color: var(--text-secondary);
          font-size: 14px;
          margin: 0;
        }

        /* Table Styles */
        .table-container {
          background: var(--bg-primary);
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-large);
          overflow: hidden;
        }

        .table-scroll {
          overflow-x: auto;
        }

        .summary-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        }

        .summary-table thead {
          background: var(--bg-secondary);
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .summary-table th {
          padding: 16px 12px;
          text-align: left;
          font-weight: 600;
          color: var(--text-secondary);
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 2px solid var(--border-secondary);
          white-space: nowrap;
        }

        .summary-table td {
          padding: 16px 12px;
          border-bottom: 1px solid var(--border-tertiary);
        }

        .data-row {
          transition: background 0.2s ease;
        }

        .data-row:hover {
          background: var(--bg-secondary);
        }

        .data-row:last-child td {
          border-bottom: none;
        }

        /* Column widths */
        .col-name { min-width: 200px; }
        .col-streak { min-width: 110px; }
        .col-total-loss { min-width: 100px; }
        .col-weight { min-width: 100px; }
        .col-daily-change { min-width: 100px; }
        .col-weekly-avg { min-width: 100px; }
        .col-weekly-change { min-width: 100px; }
        .col-boosting { min-width: 90px; }
        .col-last-note { min-width: 90px; }
        .col-activity { min-width: 160px; }
        .col-actions { min-width: 100px; text-align: right; }

        /* Name cell */
        .client-name-btn {
          background: none;
          border: none;
          color: var(--primary);
          cursor: pointer;
          padding: 0;
          text-align: left;
          transition: opacity 0.2s;
          width: 100%;
        }

        .client-name-btn:hover {
          opacity: 0.7;
        }

        .client-name-primary {
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 2px;
        }

        .client-email {
          font-size: 12px;
          color: var(--text-muted);
          font-weight: 400;
        }

        /* Placeholder cells */
        .placeholder-cell {
          display: inline-block;
          padding: 6px 12px;
          background: var(--bg-secondary);
          color: var(--text-muted);
          border-radius: 4px;
          font-size: 11px;
          font-weight: 500;
          font-style: italic;
        }

        .no-data {
          color: var(--text-muted);
          font-weight: 500;
        }

        /* Actions */
        .action-buttons {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
        }

        .action-btn {
          width: 32px;
          height: 32px;
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-medium);
          background: var(--bg-primary);
          color: var(--text-secondary);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }

        .action-btn:hover {
          background: var(--bg-secondary);
          color: var(--text-primary);
          border-color: var(--border-primary);
          transform: translateY(-1px);
        }

        .action-btn:active {
          transform: translateY(0);
        }
      `}</style>
    </>
  );
};
